import { useState, useEffect, useRef } from 'react';
import s from '../../styles/components/home/Testimonials.module.css';

const FADE = 350;
const INTERVAL = 6000;

export default function Testimonials({ data }) {
  const { sectionTitle, reviews } = data;
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState('next');
  const [animating, setAnimating] = useState(false);
  const [fill, setFill] = useState(0);
  const timerRef = useRef(null);
  const rafRef = useRef(null);

  const changeSlide = (newIndex, dir) => {
    if (newIndex === index || animating) return;
    setDirection(dir);
    setAnimating(true);
    setTimeout(() => {
      setIndex(newIndex);
      setAnimating(false);
    }, FADE);
  };

  const showNext = () => changeSlide(index === reviews.length - 1 ? 0 : index + 1, 'next');
  const showPrev = () => changeSlide(index === 0 ? reviews.length - 1 : index - 1, 'prev');

  useEffect(() => {
    timerRef.current = setInterval(showNext, INTERVAL);
    setFill(0);
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = requestAnimationFrame(() => setFill(1));
    });
    return () => {
      clearInterval(timerRef.current);
      cancelAnimationFrame(rafRef.current);
    };
  }, [index]);

  const review = reviews[index];
  const offset = direction === 'next' ? -16 : 16;
  const fade = {
    opacity: animating ? 0 : 1,
    transform: animating ? `translateX(${offset}px)` : 'translateX(0)',
    transition: `opacity ${FADE}ms ease, transform ${FADE}ms ease`,
  };

  return (
    <section className={s.container}>
      <div className={s.inner}>
        <div className={s.mark} aria-hidden="true">&ldquo;</div>

        <span className={s.label}>{sectionTitle}</span>

        <div className={s.stage}>
          <div style={fade}>
            <div className={s.stars} aria-label={`${review.rating || 5} out of 5`}>
              {'★'.repeat(review.rating || 5)}
            </div>

            <blockquote className={s.quote}>{review.quote}</blockquote>

            <div className={s.attribution}>
              <span className={s.rule} />
              <span className={s.name}>
                {review.name}{review.source ? ` · ${review.source}` : ''}
              </span>
            </div>
          </div>
        </div>

        <div className={s.controls}>
          <div className={s.track}>
            {reviews.map((_, i) => (
              <button
                key={i}
                className={s.bar}
                aria-label={`Review ${i + 1}`}
                onClick={() => changeSlide(i, i > index ? 'next' : 'prev')}
              >
                <span
                  className={s.barFill}
                  style={{
                    transform: `scaleX(${i < index ? 1 : i === index ? fill : 0})`,
                    transition: i === index && fill ? `transform ${INTERVAL}ms linear` : 'none',
                  }}
                />
              </button>
            ))}
          </div>
          <button onClick={showPrev} className={s.arrow} aria-label="Previous review">‹</button>
          <button onClick={showNext} className={s.arrow} aria-label="Next review">›</button>
        </div>
      </div>
    </section>
  );
}